import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import Container from '@components/ContentContainer';
import styles from './ActivitySidebar.module.scss';
import Contact from '@models/Contact';
import { useDispatch, useSelector } from 'react-redux';
import List from '@components/List';
import ContactAvatar from '@components/ContactAvatar';
import ListItem from '@components/ListItem';
import Section from '@components/Section';
import { setSearchTerm, sortSearch } from '@actions/search';
import { useNavigate } from 'react-router-dom';
import { CircledArrowIcon } from '@components/Icons';
import { ContactPreviewSidebar } from '@components/Contact/Sidebar';
import { Actions } from '../ActivityListItem/ItemActions/ItemActions';
import { ActivityItem, ReconnectContactStatus } from '@models/Activity';
import SharedContactPreview from '@views/SharedWithMe/SharedContactPreview';
import { selectSharedGroup } from '@reducers/sharedData/groups';
import { fetchSharedGroup } from '@actions/shared/groups';
import { useAuthContext } from '@support/hooks/use-auth-context';
import { dismissPersonFromReconnect } from '@actions/activity';
import { ID } from '@app/types';
import { metaTrigger } from '@support/keyboardHandler';
import { selectActivityItem } from '@reducers/data/activity';
import { ReminderPreviewProps } from '@components/Contact/Sidebar/ReminderPreview';

const ContactList = ({
  contacts,
  setActiveNested,
  active,
  onSelect,
  onHotKey,
  noContactActions,
  dismissPerson,
  getPersonStatus,
  noDismissal,
}: {
  contacts: Contact[];
  setActiveNested: (contact: Contact) => void;
  active: boolean;
  onSelect: (contact: Contact, newTab: boolean) => void;
  onHotKey: (contact: Contact, e: KeyboardEvent) => void;
  noContactActions?: boolean;
  dismissPerson?: (contactID: ID) => void;
  getPersonStatus?: (contactID: ID) => ReconnectContactStatus;
  noDismissal?: boolean;
}) => {
  const [skipFirstItemSelect, setSkipFirstItemSelect] = useState(true);
  const [madeMouseSelection, setMadeMouseSelection] = useState(false);

  useEffect(() => {
    if (active && skipFirstItemSelect) {
      setSkipFirstItemSelect(false);
    }
  }, [active]);

  const handleItemSelect = (item: Contact, modifier: boolean, type: string) => {
    if (!active && type !== 'click') {
      return;
    }

    return onSelect(item, modifier);
  };

  // This ensures that we don't accidentally select the first item in the list
  // when a user does a nested list selection using their mouse.
  const handleMouseSelection = () => {
    if (!madeMouseSelection) {
      setMadeMouseSelection(true);
    }
  };

  const renderItem = (contact: Contact, selected: boolean, props: any) => {
    const dismissed = getPersonStatus?.(contact.id) !== 'active';
    return (
      <ListItem
        {...props}
        selected={active && selected}
        className={cn(styles.sidebarListContact, dismissed && styles.contactDismissed)}
      >
        <ContactAvatar contact={contact} size="sm" />
        <span className={styles.contactName}>{contact.displayName}</span>
        {active && selected && !noContactActions && !dismissed && (
          <Actions
            className={styles.contactActions}
            contact={contact}
            dismissPerson={dismissPerson}
            noDismissal={noDismissal}
          />
        )}
      </ListItem>
    );
  };

  return (
    <div style={{ transform: 'translate3d(0,0,0)', backgroundColor: 'var(--bgSecondary)' }}>
      <div onMouseEnter={handleMouseSelection}>
        <List
          hotKeyPriority={1}
          skipFirstItemSelect={skipFirstItemSelect}
          onSelect={handleItemSelect}
          onActive={setActiveNested}
          items={contacts}
          renderItem={renderItem}
          // @ts-ignore
          onHotKey={onHotKey}
          active={active}
          reset={false}
          // doNotRespondToItemsUpdate
          transitionTimings={300}
          itemTransitionClassName="reconnect-person-dismiss"
        />
      </div>
    </div>
  );
};

const ContactListSidebarHeader = ({ activityItem }: { activityItem: ActivityItem }) => {
  return (
    <div className={styles.contactListHeader}>
      <div className={styles.title}>{activityItem.sidebarTitle}</div>
      <div className={styles.subtitle}>{activityItem.sidebarSubtitle}</div>
      <Section.Title underline className={styles.listTitle}>
        {activityItem.sidebarContactListTitle}
      </Section.Title>
    </div>
  );
};

const ContactListSidebarFooter = ({ activityItem }: { activityItem: ActivityItem }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const goToLatestConnections = () => {
    dispatch(sortSearch('created', 'desc'));
    dispatch(setSearchTerm(`integration:${activityItem.metadata.importSource}`, true));
    navigate('/search');
  };

  return (
    <div className={styles.contactListFooter}>
      {activityItem.type === 'import' && activityItem.metadata.importSource !== 'zapier' && (
        <button className={styles.latestConnections} onClick={goToLatestConnections}>
          <CircledArrowIcon color="textSecondary" /> See newest from{' '}
          {activityItem.metadata.importName}
        </button>
      )}
    </div>
  );
};

const ContactListSidebar = ({
  activityItem,
  setActiveNested,
  active,
  onSelect,
  onHotKey,
  focusMainList,
  noDismissal,
}: {
  activityItem: ActivityItem;
  setActiveNested: (contact: Contact) => void;
  active: boolean;
  onSelect: (contact: Contact, newTab: boolean) => void;
  onHotKey: (contact: Contact, e: KeyboardEvent) => void;
  focusMainList: () => void;
  noDismissal?: boolean;
}) => {
  const dispatch = useDispatch();
  let dismissPerson = undefined;

  if (activityItem.type === 'reconnect') {
    dismissPerson = (contactID: ID) => {
      dispatch(dismissPersonFromReconnect(activityItem, contactID));

      if (activityItem.filteredContacts.length === 1) {
        return focusMainList();
      }
    };
  }

  const getPersonStatus = (contactID: ID) => {
    return activityItem.contactStatus(contactID);
  };

  return (
    <Container.Sidebar className={styles.sidebarContent} key={activityItem.id}>
      <ContactListSidebarHeader activityItem={activityItem} />
      <ContactList
        contacts={activityItem.metadata.contacts}
        setActiveNested={setActiveNested}
        active={active}
        onSelect={onSelect}
        onHotKey={(contact, e) => {
          if (
            !noDismissal &&
            activityItem.type === 'reconnect' &&
            e.key === 'e' &&
            !metaTrigger(e)
          ) {
            e.preventDefault();
            dismissPerson(contact.id);
            return;
          }
          onHotKey(contact, e);
        }}
        dismissPerson={dismissPerson}
        getPersonStatus={getPersonStatus}
        noDismissal={noDismissal}
      />
      <ContactListSidebarFooter activityItem={activityItem} />
    </Container.Sidebar>
  );
};

const SharedGroupPreview = ({
  activityItem,
  setActiveNested,
  active,
  onSelect,
  onHotKey,
}: {
  activityItem: ActivityItem;
  setActiveNested: (contact: Contact) => void;
  active: boolean;
  onSelect: (contact: Contact, newTab: boolean) => void;
  onHotKey: (contact: Contact, e: KeyboardEvent) => void;
}) => {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const uuid = activityItem?.metadata?.sharedGroup.id;
  const sharedGroup = useSelector(selectSharedGroup(uuid));

  const itemWithFetchedGroup = useMemo(() => {
    return activityItem.copyWith({ metadata: { ...activityItem.metadata, sharedGroup } });
  }, [sharedGroup]);

  useEffect(() => {
    dispatch(fetchSharedGroup(uuid, user));
  }, [uuid]);

  return (
    <Container.Sidebar className={styles.sidebarContent} key={uuid}>
      <ContactListSidebarHeader activityItem={itemWithFetchedGroup} />
      <ContactList
        contacts={sharedGroup?.contactsMap?.valueSeq().toArray() || []}
        setActiveNested={setActiveNested}
        active={active}
        onSelect={onSelect}
        onHotKey={onHotKey}
        noContactActions
      />
    </Container.Sidebar>
  );
};

export function ActivitySidebar({
  activityItemID,
  setActiveNested,
  active,
  onHotKey,
  onSelect,
  focusMainList,
  noDismissal,
}: {
  activityItemID: ID;
  setActiveNested: (contact: Contact) => void;
  active: boolean;
  onHotKey: (contact: Contact, e: KeyboardEvent) => void;
  onSelect: (contact: Contact, newTab: boolean) => void;
  focusMainList: () => void;
  noDismissal?: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const activityItem = useSelector(selectActivityItem(activityItemID));
  const contactID = activityItem?.contact?.id;
  const isSelf = String(user?.contact_id) === String(contactID);
  const reminder: ReminderPreviewProps =
    activityItem?.type === 'reminder'
      ? {
          content: activityItem?.metadata.content,
          date: activityItem?.date,
          id: activityItem?.metadata.noteId,
          contactID,
        }
      : undefined;
  const sharedContact = activityItem?.metadata?.sharedContact;
  const sharedGroup = activityItem?.metadata?.sharedGroup;

  if (sharedContact) {
    return <SharedContactPreview contactUUID={sharedContact.id} />;
  }

  if (sharedGroup) {
    return (
      <SharedGroupPreview
        key={activityItem.id}
        activityItem={activityItem}
        setActiveNested={setActiveNested}
        active={active}
        onSelect={(contact) => navigate(`/share/contact/${contact.id}`)}
        onHotKey={onHotKey}
      />
    );
  }

  if (contactID && !isSelf) {
    return (
      <ContactPreviewSidebar
        key={contactID}
        contactID={contactID}
        reminder={reminder}
        includeHighlights={activityItem?.type === 'news'}
        includeBirthdaySuggestions={activityItem?.type === 'birthday'}
      />
    );
  }

  if (activityItem?.metadata?.contacts?.length > 1) {
    return (
      <ContactListSidebar
        key={activityItem.id}
        activityItem={activityItem}
        setActiveNested={setActiveNested}
        active={active}
        onSelect={onSelect}
        onHotKey={onHotKey}
        focusMainList={focusMainList}
        noDismissal={noDismissal}
      />
    );
  }

  return <Container.Sidebar />;
}
